import { useState, useEffect, FC } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import ApplicantSelectionDropdown from "../ApplicantSelectionDropdown";
import { LoanApplicationService } from "../../../services/LoanApplication";

import {
  Owner,
  createNewOwner,
  convertToOwnershipData,
} from "../../../shared/utils/ownershipUtils";
import Button from "../../atomic/Button";

interface OwnerShipBreakdownFormData {
  owners: Owner[];
}

interface OwnerShipBreakdownEditModalProps {
  onClose?: () => void;
  defaultValue?: any;
  loanDetails: any;
  loanApplicationID: string;
  userID: string;
  token: string;
  pageName: string;
  notify: (params: {
    text: string;
    title: string;
    type: "success" | "error" | "warning" | "info";
  }) => void;
  isPlatformDashboard: boolean;
  isMFLLAP: boolean;
}

const OwnerShipBreakdownEditModal: FC<OwnerShipBreakdownEditModalProps> = ({
  onClose,
  defaultValue,
  loanDetails,
  loanApplicationID,
  userID,
  token,
  pageName,
  notify,
  isPlatformDashboard = false,
  isMFLLAP = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [deletedExistingOwners, setDeletedExistingOwners] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<OwnerShipBreakdownFormData>({
    defaultValues: {
      owners: [
        {
          id: "",
          name: null,
          status: "ACTIVE",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "owners",
    keyName: "fieldId",
  });

  const watchedOwners = watch("owners");

  const fetchOwnershipData = async () => {
    try {
      setIsInitialLoading(true);
      if (defaultValue && Array.isArray(defaultValue)) {
        const convertedData = convertToOwnershipData(defaultValue);
        console.log(
          "Converting data returned from UI config to component data:",
          convertedData,
        );

        const existingOwners = convertedData?.map((owner: any) => ({
          id: owner.id || "",
          name: owner.name || "",
        }));

        console.log("Existing owners from API:", existingOwners);

        if (existingOwners && existingOwners.length > 0) {
          // Match owners with applicants from loanDetails
          const matchedOwners = existingOwners.map(
            (owner: { id: string; name: string }) => {
              const matchedApplicant = findApplicantByName(owner.name);
              return {
                id: owner.id,
                name: {
                  name: matchedApplicant?.name || "", //TODO Need to check while pre-filing
                  value: matchedApplicant?.value || "",
                },
                status: "ACTIVE" as const,
              };
            },
          );

          reset({ owners: matchedOwners });
        } else {
          // If no existing owners, start with one empty owner
          reset({
            owners: [
              {
                id: "",
                name: null,
                status: "ACTIVE" as const,
              },
            ],
          });
        }
      }
    } catch (error) {
      console.error("Error fetching ownership data:", error);
      notify({
        title: "Error",
        type: "error",
        text: "Failed to load existing ownership data",
      });
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchOwnershipData();
  }, [defaultValue, reset]);

  const findApplicantByName = (name: string) => {
    if (!loanDetails) return null;

    // Check primary applicant
    if (loanDetails.name === name) {
      return {
        name: `${loanDetails.name} - ${loanDetails.mobile} (Primary)`,
        value: loanDetails.userID,
      };
    }

    // Check co-applicants
    const coApplicants = loanDetails?.coApplicantDetails || [];
    for (const coApplicant of coApplicants) {
      if (coApplicant.name === name) {
        return {
          name: `${coApplicant.name} - ${coApplicant.mobile} (Co-Applicant)`,
          value: coApplicant.userID,
        };
      }
    }

    return null;
  };

  // Get all available applicants for dropdown
  const getAllApplicants = () => {
    if (!loanDetails) return [];

    const applicants = [];

    // Add primary applicant
    if (loanDetails.name && loanDetails.mobile) {
      applicants.push({
        name: `${loanDetails.name} - ${loanDetails.mobile} (Primary)`,
        value: loanDetails.userID,
      });
    }

    // Add co-applicants
    const coApplicants = loanDetails?.coApplicantDetails || [];
    coApplicants.forEach(
      (coApplicant: { name?: string; mobile?: string; userID?: string }) => {
        if (coApplicant.name && coApplicant.mobile) {
          applicants.push({
            name: `${coApplicant.name} - ${coApplicant.mobile} (Co-Applicant)`,
            value: coApplicant.userID || "",
          });
        }
      },
    );

    return applicants;
  };

  // Get available applicants excluding already selected ones
  const getAvailableApplicants = (currentOwnerIndex: number) => {
    const allApplicants = getAllApplicants();
    const watchedOwners = watch("owners");

    if (!watchedOwners) return allApplicants;

    // Get currently selected applicants (excluding the current owner)
    const selectedApplicants = watchedOwners
      .map((owner, index) => ({ owner, index }))
      .filter(
        ({ owner, index }) =>
          index !== currentOwnerIndex && owner.name && owner.name.value,
      )
      .map(({ owner }) => owner.name?.value || "");

    // Filter out already selected applicants
    return allApplicants.filter(
      (applicant) => !selectedApplicants.includes(applicant.value),
    );
  };

  // Check if we can add more owners
  const canAddMoreOwners = () => {
    const allApplicants = getAllApplicants();
    const currentOwners = watch("owners") || [];
    return currentOwners.length < allApplicants.length;
  };

  // Get warning message for max owners
  const getMaxOwnersWarning = () => {
    const allApplicants = getAllApplicants();
    const currentOwners = watch("owners") || [];

    if (currentOwners.length >= allApplicants.length) {
      return `Maximum number of owners reached (${allApplicants.length}). Cannot add more owners.`;
    }
    return null;
  };

  const handleAddOwner = () => {
    if (!canAddMoreOwners()) {
      notify({
        title: "Warning",
        type: "warning",
        text: getMaxOwnersWarning() || "Cannot add more owners",
      });

      return;
    }

    const newOwner = createNewOwner();
    append({
      ...newOwner,
      name: null,
    });
  };

  const deleteOwner = (index: number) => {
    if (fields.length > 1) {
      const ownerToDelete = watchedOwners[index];

      // Check if this is an existing owner (has backend ID)
      if (ownerToDelete?.id && ownerToDelete?.id !== "") {
        // This is an existing owner - track it for deletion
        setDeletedExistingOwners((prev) => [
          ...prev,
          {
            id: ownerToDelete?.id,
            name: ownerToDelete?.name?.name || "",
            status: "INACTIVE", // Mark for deletion
          },
        ]);
      }
      // If it's a new owner (no ID), just remove from UI
      remove(index);
    }
  };

  const onSubmit = async (data: OwnerShipBreakdownFormData) => {
    if (data.owners.length === 0) {
      console.error("Please add at least one owner");
      notify({
        title: "Validation Error",
        type: "error",
        text: "Please add at least one owner",
      });

      return;
    }

    // Validate that all owners have selected applicants
    const hasUnselectedOwners = data.owners.some(
      (owner) => !owner.name || !owner.name.name || !owner.name.value,
    );
    if (hasUnselectedOwners) {
      notify({
        title: "Validation Error",
        type: "error",
        text: "Please select an owner for all entries",
      });
      return;
    }

    try {
      setLoading(true);

      // Prepare active owners (current form data)
      const activeOwners = data.owners.map((owner) => ({
        ...(owner?.id && owner?.id !== "" && { id: owner?.id }),
        name: owner.name?.name || "",
        status: owner.status || "ACTIVE",
        userID: owner.name?.value,
      }));

      const allOwners = [...activeOwners, ...deletedExistingOwners];

      const payload = {
        userID,
        identifier: "collateral_ownership" as const,
        metadata: allOwners,
      };

      

      const response = await LoanApplicationService.upsertMetadata({
        token,
        pageName,
        payload,
        isLenderDashboard: isPlatformDashboard,
        isMFL_LAP: isMFLLAP!,
      });

      if (response.status) {
        notify({
          title: "Success",
          type: "success",
          text:
            (response?.data as any)?.message ||
            "Ownership data saved successfully",
        });
        onClose?.();
      } else {
        console.error("Failed to save ownership data:", response?.data);
        notify({
          title: "Error",
          type: "error",
          text:
            (response?.data as any)?.message ||
            response.error ||
            "Failed to save ownership data",
        });
      }
    } catch (error) {
      console.error("Failed to save ownership data:", error);
      notify({
        title: "Error",
        type: "error",
        text: "Failed to save ownership data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  return (
    <div className="max-w-4xl mx-auto bg-gray-50 h-full flex flex-col min-h-0">
      <div
        className="p-6 pb-32 flex-1 overflow-y-auto min-h-0"
        style={{ maxHeight: "calc(90vh - 140px)" }}
      >
        {isInitialLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">
                Loading ownership data...
              </p>
            </div>
          </div>
        ) : (
          <>

            <div className="space-y-4">
              {fields.map((owner, index) => (
                <div
                  key={owner.fieldId}
                  className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-indigo-100 border border-indigo-200 rounded-md flex items-center justify-center text-sm font-medium text-indigo-700">
                        {index + 1}
                      </div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Owner {index + 1}
                      </h3>
                    </div>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => deleteOwner(index)}
                        className="text-red-500 hover:text-red-700 transition-colors p-1 rounded-md hover:bg-red-50"
                        title="Delete owner"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Owner <span className="text-red-500">*</span>
                    </label>
                    <Controller
                      control={control}
                      name={`owners.${index}.name`}
                      rules={{ required: "Please select an owner" }}
                      render={({ field }) => (
                        <ApplicantSelectionDropdown
                          loanDetails={loanDetails}
                          onChange={(applicant: any) => {
                            field.onChange(applicant);
                          }}
                          selectedApplicant={field.value}
                          setSelectedApplicant={(applicant: any) => {
                            field.onChange(applicant);
                          }}
                          styles={{ container: "!w-full" }}
                          resetApplicantState={false}
                          availableApplicants={getAvailableApplicants(index)}
                        />
                      )}
                    />
                    {errors.owners?.[index]?.name && (
                      <p className="text-red-500 text-xs mt-1">
                        Please select an owner
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleAddOwner}
                disabled={!canAddMoreOwners()}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md transition-colors shadow-sm ${
                  canAddMoreOwners()
                    ? "text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    : "text-gray-400 bg-gray-100 cursor-not-allowed"
                }`}
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Owner
              </button>

              {getMaxOwnersWarning() && (
                <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="text-sm text-yellow-800">
                    {getMaxOwnersWarning()}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="bg-white border-t border-gray-200 px-6 py-4 shadow-lg z-50 sticky bottom-0 left-0 right-0">
        <div className="max-w-4xl mx-auto flex justify-end items-center">
          <div className="flex space-x-3">
            <Button
              onClick={handleClose}
              disabled={loading}
            >
              Close
            </Button>
            <Button
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={
                loading ||
                fields.length === 0 ||
                watchedOwners?.some(
                  (owner) =>
                    !owner.name || !owner.name.name || !owner.name.value,
                )
              }
            >
              {loading ? "Saving..." : "Submit"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerShipBreakdownEditModal;
